/**
 * Dates and times, in the reader's language.
 *
 * These wrap `Intl` and take the locale from the runtime rather than from the
 * device: `toLocaleDateString(undefined, …)` reads `navigator.language`, which
 * is not what the switcher just set, and it is what every one of these call
 * sites did before.
 *
 * The stored value never changes shape. Schedule times are ZONE-LESS LOCAL
 * strings (`"2026-09-05T09:00"`, `shared/time.ts`), parsed with `parseLocal`
 * and rendered from local parts; nothing here calls `toISOString()`, and no
 * `Intl` option that could introduce a time zone is passed. Ukrainian months
 * come out in the genitive on their own ("5 вересня") because `Intl` knows
 * the day-plus-month form.
 */

import { parseLocal } from "../../shared/time";
import { englishOrdinal } from "../../shared/types";
import { getLocale } from "./runtime";

/**
 * Ukrainian NEUTER ordinal endings, by last digit, for the noun «місце».
 *
 * Not `Intl.PluralRules` with `type: "ordinal"`: CLDR gives Ukrainian a single
 * ordinal category, so it can only ever answer "other", and the ending does
 * not follow the last digit alone in the way English's does anyway.
 */
const UK_ORDINAL: Readonly<Record<number, string>> = {
  1: "ше",
  2: "ге",
  3: "тє",
  7: "ме",
  8: "ме",
};

/** A finishing place as an ordinal: "5th" or «5-те». The English side is
 *  `shared/types.ts`'s own, so the canonical note and the screen agree. */
export function ordinal(n: number): string {
  if (getLocale() !== "uk") return englishOrdinal(n);
  const teen = n % 100;
  if (teen >= 11 && teen <= 19) return `${n}-те`;
  // The one tens-word that does not end in «-те»: «сорокове». Every other
  // round ten does (двадцяте, тридцяте, п'ятдесяте … дев'яносте, соте), so
  // the last-digit table with a «те» fallback was right everywhere but here —
  // «Матч за 40-ве місце», not «40-те».
  if (teen === 40) return `${n}-ве`;
  return `${n}-${UK_ORDINAL[n % 10] ?? "те"}`;
}

/** One formatter per (locale, shape). Building an `Intl.DateTimeFormat` is the
 *  expensive part, and a schedule renders hundreds of rows. */
const cache = new Map<string, Intl.DateTimeFormat>();

function fmt(key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = getLocale();
  const id = `${locale}:${key}`;
  let f = cache.get(id);
  if (f === undefined) {
    f = new Intl.DateTimeFormat(locale, options);
    cache.set(id, f);
  }
  return f;
}

/** 24-hour, both locales: Ukraine writes 18:00 and so does the rest of this
 *  app's UI. Built from local parts rather than `Intl` so it can never pick up
 *  a zone. */
export function clockOf(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** "сб" / "Sat". */
export function weekdayShort(d: Date): string {
  return fmt("wd-short", { weekday: "short" }).format(d);
}

/** "5 вер." / "5 Sep". */
export function dayShort(d: Date): string {
  return fmt("day-short", { day: "numeric", month: "short" }).format(d);
}

/** "субота, 5 вересня" / "Saturday, 5 September". */
export function dayLong(d: Date): string {
  return fmt("day-long", { weekday: "long", day: "numeric", month: "long" }).format(d);
}

// ---------- the shapes the surfaces actually ask for ----------

/** "сб 18:00": a schedule row on a phone. Falls back to the raw string when
 *  the stored value is not a time at all, which is what every one of these did
 *  before and is still the right answer at a pitch. */
export function shortWhen(startAt: string): string {
  const d = parseLocal(startAt);
  if (d === null) return startAt;
  return `${weekdayShort(d)} ${clockOf(d)}`;
}

/** "субота, 5 вересня, 18:00": the confirmation line under a picker. */
export function longWhen(startAt: string): string {
  const d = parseLocal(startAt);
  if (d === null) return startAt;
  return `${dayLong(d)}, ${clockOf(d)}`;
}

/** One date shape for event facts, optionally followed by a 24-hour time.
 * The year stays on both endpoints; weekday names belong to schedule headings. */
export function eventWhen(value: string): string {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = parseLocal(dateOnly ? `${value}T12:00` : value);
  if (date === null) return value;
  const label = fmt("event-date", { day: "numeric", month: "short", year: "numeric" }).format(date);
  return dateOnly ? label : `${label}, ${clockOf(date)}`;
}

/** Sunday first, as `Date.getDay()` counts. */
const UK_WEEKDAY_GENITIVE = [
  "неділі",
  "понеділка",
  "вівторка",
  "середи",
  "четверга",
  "п'ятниці",
  "суботи",
] as const;

/** "до суботи, 5 вересня, 18:00": a deadline after «до» / "by". Ukrainian
 *  wants the weekday in the genitive there, which `Intl` never gives («до
 *  неділя, 6 вересня» read on the spirit page until 2026-09-16); English is
 *  `longWhen`. */
export function deadlineWhen(startAt: string): string {
  const d = parseLocal(startAt);
  if (d === null) return startAt;
  if (getLocale() !== "uk") return longWhen(startAt);
  const day = fmt("day-month", { day: "numeric", month: "long" }).format(d);
  return `${UK_WEEKDAY_GENITIVE[d.getDay()]}, ${day}, ${clockOf(d)}`;
}

/** "5 вер. 18:00": an audit or submission timestamp, which IS a real instant
 *  and so comes in as epoch milliseconds rather than a zone-less string. */
export function stampWhen(at: number): string {
  const d = new Date(at);
  return `${dayShort(d)} ${clockOf(d)}`;
}

/** "СБ 5 ВЕР": the board's day heading. Uppercased for the band; NXT has the
 *  Cyrillic, so this is display type in either language. */
export function dayHeading(day: string): string {
  const d = parseLocal(`${day}T12:00`);
  if (d === null) return day;
  return `${weekdayShort(d)} ${dayShort(d)}`.toUpperCase();
}

/** "Сб, 5 вер, 18:00": what `DateTimeField` prints under the control so the
 *  field always says what it holds, whatever the platform draws. */
export function fieldStamp(d: Date): string {
  return fmt("stamp", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** "Сб, 5 вер. 2026": what `DateField` prints under its control, for the
 *  same reason `fieldStamp` exists. */
export function fieldDate(d: Date): string {
  return fmt("date", { weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(
    d,
  );
}

/**
 * "5–6 вер. 2026" / "5–6 Sep 2026": a tournament's span, from two zone-less
 * `YYYY-MM-DD` strings. Noon local, like `dayHeading`, so no zone can move
 * the day; the raw pair when either is not a date at all.
 */
export function dateRangeOf(start: string, end: string): string {
  const a = parseLocal(`${start}T12:00`);
  const b = parseLocal(`${end}T12:00`);
  if (a === null || b === null) return `${start} – ${end}`;
  return fmt("range", { day: "numeric", month: "short", year: "numeric" }).formatRange(a, b);
}

/** "18:00": a row of games' own clock, on a surface that has already said
 *  which day it is. `null` when the stored value is not a time — the caller
 *  decides what an unreadable row prints, and printing the raw string in a
 *  56px gutter is not it. */
export function clockWhen(startAt: string): string | null {
  const d = parseLocal(startAt);
  return d === null ? null : clockOf(d);
}
